import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
	CheckCircle,
	Edit,
	Eye,
	EyeOff,
	Search,
	Trash2,
	User,
	UserCheck,
	UserMinus,
	UserPlus,
	Users2,
	XCircle,
} from 'lucide-react';
import Button from './ui/Button';
import { toast } from 'sonner';
import PermissionGuard from './PermissionGuard';
import FilterDropdown from './common/FilterDropdown';
import DashboardHeader from './dashboard/DashboardHeader';

// Define interfaces
interface User {
	id: string;
	email: string;
	full_name: string;
	role: string;
	status: 'active' | 'inactive';
	created_at: string;
	last_login?: string;
	user_id?: string; // Link to auth.users table
}

interface UserFormData {
	email: string;
	full_name: string;
	password: string;
	role: string;
}

interface EditUserFormData {
	id: string;
	email: string;
	full_name: string;
	role: string;
}

interface UserStatCardProps {
	label: string;
	value: string;
	icon: React.ReactNode;
}

interface UserTableProps {
	users: User[];
	loading: boolean;
	toggleUserStatus: (userId: string) => Promise<void>;
	showDeleteConfirmation: (userId: string) => void;
	showEditModal: (user: User) => void;
	formatDate: (dateString: string) => string;
	currentPage: number;
	totalPages: number;
	setCurrentPage: (page: number) => void;
}

interface UserFormModalProps {
	showModal: boolean;
	setShowModal: (show: boolean) => void;
	formData: UserFormData;
	setFormData: React.Dispatch<React.SetStateAction<UserFormData>>;
	handleSubmit: (e: React.FormEvent) => Promise<void>;
	loading: boolean;
	passwordVisible: boolean;
	togglePasswordVisibility: () => void;
}

interface DeleteModalProps {
	deleteModalOpen: boolean;
	setDeleteModalOpen: (show: boolean) => void;
	userToDelete: string | null;
	handleDeleteUser: (userId: string) => Promise<void>;
}

interface EditUserModalProps {
	showEditModal: boolean;
	setShowEditModal: (show: boolean) => void;
	editFormData: EditUserFormData;
	setEditFormData: React.Dispatch<React.SetStateAction<EditUserFormData>>;
	handleEditSubmit: (e: React.FormEvent) => Promise<void>;
	loading: boolean;
}

interface UserFiltersProps {
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	statusFilter: string;
	setStatusFilter: (status: string) => void;
	roleFilter: string;
	setRoleFilter: (role: string) => void;
	dateFilter: string;
	setDateFilter: (date: string) => void;
	clearFilters: () => void;
}

// Component for displaying user statistics cards
const UserStatCard: React.FC<UserStatCardProps> = ({ label, value, icon }) => {
	return (
		<div className="bg-white shadow-md rounded-lg p-4 flex flex-col justify-center">
			<div className="flex justify-between items-center">
				<span className="text-sm text-gray-500">{label}</span>
				<div className="p-2 bg-gray-100 rounded-full">{icon}</div>
			</div>
			<p className="text-2xl font-bold mt-2">{value}</p>
		</div>
	);
};

// Component for user filtering controls
const UserFilters: React.FC<UserFiltersProps> = ({
	searchTerm,
	setSearchTerm,
	statusFilter,
	setStatusFilter,
	roleFilter,
	setRoleFilter,
	dateFilter,
	setDateFilter,
	clearFilters,
}) => {
	return (
		<div className="py-4 bg-white rounded-lg shadow-sm px-4 mb-6">
			<div className="flex flex-wrap gap-4 items-center">
				<div className="flex-1 min-w-[200px]">
					<div className="relative">
						<input
							type="text"
							value={searchTerm}
							onChange={e => setSearchTerm(e.target.value)}
							placeholder="Search users..."
							className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg"
						/>
						<div className="absolute top-0 left-0 h-full flex items-center pl-3 text-gray-400">
							<Search className="w-4 h-4" />
						</div>
					</div>
				</div>

				<FilterDropdown
					triggerLabel="Status"
					selectedValue={statusFilter}
					onChange={setStatusFilter}
					options={[
						{ label: 'All', value: '' },
						{ label: 'Active', value: 'active' },
						{ label: 'Inactive', value: 'inactive' },
					]}
				/>

				<FilterDropdown
					triggerLabel="Role"
					selectedValue={roleFilter}
					onChange={setRoleFilter}
					options={[
						{ label: 'All', value: '' },
						{ label: 'Admin', value: 'admin' },
						{ label: 'Super Admin', value: 'super_admin' },
						{ label: 'Manager', value: 'manager' },
						{ label: 'Auditor', value: 'auditor' },
						{ label: 'Field Agent', value: 'field_agent' },
					]}
				/>

				<FilterDropdown
					triggerLabel="Date"
					selectedValue={dateFilter}
					onChange={setDateFilter}
					options={[
						{ label: 'All Time', value: '' },
						{ label: 'Last 7 Days', value: '7days' },
						{ label: 'Last 30 Days', value: '30days' },
						{ label: 'Last 90 Days', value: '90days' },
					]}
				/>

				{(searchTerm || statusFilter || roleFilter || dateFilter) && (
					<button
						onClick={clearFilters}
						className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg"
					>
						Clear Filters
					</button>
				)}
			</div>
		</div>
	);
};

// Modal component for confirming user deletion
const DeleteModal: React.FC<DeleteModalProps> = ({
	deleteModalOpen,
	setDeleteModalOpen,
	userToDelete,
	handleDeleteUser,
}) => {
	return (
		deleteModalOpen && (
			<div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
				<div className="bg-white w-full max-w-md mx-auto rounded-xl shadow-lg overflow-hidden">
					<div className="p-6">
						<div className="flex justify-between items-center mb-4">
							<h2 className="text-xl font-bold text-red-600">Confirm Delete</h2>
							<button
								onClick={() => setDeleteModalOpen(false)}
								className="text-gray-400 hover:text-gray-600"
							>
								&times;
							</button>
						</div>
						<p className="text-gray-600 mb-6">
							Are you sure you want to delete this user? This action cannot be undone.
						</p>
						<div className="flex justify-end gap-3">
							<button
								onClick={() => setDeleteModalOpen(false)}
								className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
							>
								Cancel
							</button>
							<button
								onClick={() => {
									if (userToDelete) {
										handleDeleteUser(userToDelete);
									}
									setDeleteModalOpen(false);
								}}
								className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
							>
								Delete User
							</button>
						</div>
					</div>
				</div>
			</div>
		)
	);
};

// Modal component for editing user details
const EditUserModal: React.FC<EditUserModalProps> = ({
	showEditModal,
	setShowEditModal,
	editFormData,
	setEditFormData,
	handleEditSubmit,
	loading,
}) => {
	return (
		showEditModal && (
			<div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
				<div className="bg-white w-full max-w-md mx-auto rounded-xl shadow-lg overflow-hidden">
					<div className="p-6">
						<div className="flex justify-between items-center mb-4">
							<h2 className="text-xl font-bold text-blue-600">Edit User</h2>
							<button
								onClick={() => setShowEditModal(false)}
								className="text-gray-400 hover:text-gray-600"
							>
								&times;
							</button>
						</div>
						<form onSubmit={handleEditSubmit} className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700">Full Name</label>
								<input
									type="text"
									value={editFormData.full_name}
									onChange={e =>
										setEditFormData(prev => ({
											...prev,
											full_name: e.target.value,
										}))
									}
									required
									className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700">Email</label>
								<input
									type="email"
									value={editFormData.email}
									onChange={e => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
									required
									className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								/>
							</div>

							<div>
								<label className="block text-sm font-medium text-gray-700">Role</label>
								<select
									value={editFormData.role}
									onChange={e => setEditFormData(prev => ({ ...prev, role: e.target.value }))}
									required
									className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
								>
									<option value="">Select Role</option>
									<option value="admin">Admin</option>
									<option value="operator">Operator</option>
									<option value="viewer">Viewer</option>
								</select>
							</div>

							<div className="flex justify-end space-x-2">
								<button
									type="button"
									onClick={() => setShowEditModal(false)}
									className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={loading}
									className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
								>
									{loading ? (
										<>
											<span className="mr-2">Updating</span>
											<div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
										</>
									) : (
										'Update User'
									)}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		)
	);
};

// User table component to display user data with pagination
const UserTable: React.FC<UserTableProps> = ({
	users,
	loading,
	toggleUserStatus,
	showDeleteConfirmation,
	showEditModal,
	formatDate,
	currentPage,
	totalPages,
	setCurrentPage,
}) => {
	return (
		<>
			<div className="bg-white rounded-xl shadow-sm overflow-hidden">
				<div className="overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr className="bg-gray-50 text-left">
								<th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
									User
								</th>
								<th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
									Role
								</th>
								<th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
									Status
								</th>
								<th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
									Created
								</th>
								<th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
									Actions
								</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-gray-200">
							{loading ? (
								// Loading skeleton
								[...Array(5)].map((_, index) => (
									<tr key={`skeleton-${index}`}>
										<td className="px-6 py-4">
											<div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
											<div className="h-3 bg-gray-200 rounded w-1/2 mt-2 animate-pulse"></div>
										</td>
										<td className="px-6 py-4">
											<div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
										</td>
										<td className="px-6 py-4">
											<div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
										</td>
										<td className="px-6 py-4">
											<div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
										</td>
										<td className="px-6 py-4">
											<div className="h-8 bg-gray-200 rounded w-24 animate-pulse"></div>
										</td>
									</tr>
								))
							) : users.length === 0 ? (
								// No data message
								<tr>
									<td colSpan={5} className="px-6 py-8 text-center text-gray-500">
										No users found. Try adjusting your filters or create a new user.
									</td>
								</tr>
							) : (
								// User data rows
								users.map(user => (
									<tr key={user.id}>
										<td className="px-6 py-4">
											<div className="flex items-center">
												<div className="ml-4">
													<div className="text-sm font-medium text-gray-900">{user.full_name}</div>
													<div className="text-sm text-gray-500">{user.email}</div>
												</div>
											</div>
										</td>
										<td className="px-6 py-4">
											<span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
												{user.role}
											</span>
										</td>
										<td className="px-6 py-4">
											<span
												className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
													user.status === 'active'
														? 'bg-green-100 text-green-800'
														: 'bg-red-100 text-red-800'
												}`}
											>
												{user.status === 'active' ? (
													<>
														<CheckCircle className="mr-1 h-3 w-3" />
														Active
													</>
												) : (
													<>
														<XCircle className="mr-1 h-3 w-3" />
														Inactive
													</>
												)}
											</span>
										</td>
										<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
											{formatDate(user.created_at)}
										</td>
										<td className="px-6 py-4 text-right text-sm font-medium">
											<div className="flex items-center space-x-2">
												<PermissionGuard permission="users.read">
													<button
														onClick={() => showEditModal(user)}
														className="text-indigo-600 hover:text-indigo-900 border border-indigo-300 rounded-md px-2 py-1"
													>
														<Edit className="h-4 w-4" />
													</button>
												</PermissionGuard>
												<PermissionGuard permission="users.read">
													<button
														onClick={() => toggleUserStatus(user.id)}
														className="text-blue-600 hover:text-blue-900 border border-blue-300 rounded-md px-2 py-1"
													>
														{user.status === 'active' ? 'Deactivate' : 'Activate'}
													</button>
												</PermissionGuard>
												<PermissionGuard permission="users.delete">
													<button
														onClick={() => showDeleteConfirmation(user.id)}
														className="text-red-600 hover:text-red-900 border border-red-300 rounded-md px-2 py-1"
													>
														<Trash2 className="h-4 w-4" />
													</button>
												</PermissionGuard>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</div>

			{/* Pagination controls */}
			<div className="flex justify-between items-center mt-4 px-2">
				<div className="text-sm text-gray-700">
					Showing page {currentPage} of {totalPages}
				</div>
				<div className="flex space-x-2">
					<button
						onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
						disabled={currentPage === 1}
						className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
					>
						Previous
					</button>
					<button
						onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
						disabled={currentPage === totalPages}
						className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
					>
						Next
					</button>
				</div>
			</div>
		</>
	);
};

// Modal component for creating a new user
const UserFormModal: React.FC<UserFormModalProps> = ({
	showModal,
	setShowModal,
	formData,
	setFormData,
	handleSubmit,
	loading,
	passwordVisible,
	togglePasswordVisibility,
}) => {
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value,
		}));
	};

	return (
		showModal && (
			<div className="fixed inset-0 bg-black/20 z-50 flex items-center justify-center">
				<div className="bg-white w-full max-w-md mx-auto rounded-xl shadow-lg overflow-hidden">
					<div className="p-6">
						<div className="flex justify-between items-center mb-4">
							<h2 className="text-xl font-bold">Create New User</h2>
							<button
								onClick={() => setShowModal(false)}
								className="text-gray-400 hover:text-gray-600"
							>
								&times;
							</button>
						</div>

						<form onSubmit={handleSubmit}>
							<div className="space-y-4">
								<div>
									<label
										htmlFor="full_name"
										className="block text-sm font-medium text-gray-700 mb-1"
									>
										Full Name
									</label>
									<input
										id="full_name"
										name="full_name"
										type="text"
										value={formData.full_name}
										onChange={handleInputChange}
										className="w-full px-4 py-2 border border-gray-300 rounded-lg"
										required
									/>
								</div>
								<div>
									<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
										Email Address
									</label>
									<input
										id="email"
										name="email"
										type="email"
										value={formData.email}
										onChange={handleInputChange}
										className="w-full px-4 py-2 border border-gray-300 rounded-lg"
										required
									/>
								</div>
								<div>
									<label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
										Role
									</label>
									<select
										id="role"
										name="role"
										value={formData.role}
										onChange={handleInputChange}
										className="w-full px-4 py-2 border border-gray-300 rounded-lg"
										required
									>
										<option value="">Select a role</option>
										<option value="field_agent">Field Agent</option>
										<option value="auditor">Auditor</option>
										<option value="manager">Manager</option>
										<option value="admin">Admin</option>
									</select>
								</div>
								<div>
									<label
										htmlFor="password"
										className="block text-sm font-medium text-gray-700 mb-1"
									>
										Password
									</label>
									<div className="relative">
										<input
											id="password"
											name="password"
											type={passwordVisible ? 'text' : 'password'}
											value={formData.password}
											onChange={handleInputChange}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-10"
											minLength={6}
											required
										/>
										<button
											type="button"
											className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
											onClick={togglePasswordVisibility}
										>
											{passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
										</button>
									</div>
								</div>
							</div>
							<div className="mt-6 flex justify-end gap-3">
								<button
									type="button"
									onClick={() => setShowModal(false)}
									className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
								>
									Cancel
								</button>
								<Button
									type="submit"
									disabled={loading}
									className="px-4 py-2 bg-ghana-red hover:bg-red-700 text-white rounded-lg"
								>
									{loading ? 'Creating...' : 'Create User'}
								</Button>
							</div>
						</form>
					</div>
				</div>
			</div>
		)
	);
};

// Main Users component that integrates all subcomponents
const Users: React.FC = () => {
	// State for users and UI
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [totalActive, setTotalActive] = useState<number>(0);
	const [totalInactive, setTotalInactive] = useState<number>(0);

	// State for user creation modal
	const [showModal, setShowModal] = useState<boolean>(false);
	const [formData, setFormData] = useState<UserFormData>({
		full_name: '',
		email: '',
		password: '',
		role: '',
	});
	const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

	// State for edit user modal
	const [showEditModal, setShowEditModal] = useState<boolean>(false);
	const [editFormData, setEditFormData] = useState<EditUserFormData>({
		id: '',
		full_name: '',
		email: '',
		role: '',
	});

	// State for delete confirmation
	const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
	const [userToDelete, setUserToDelete] = useState<string | null>(null);

	// Filter states
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [statusFilter, setStatusFilter] = useState<string>('');
	const [roleFilter, setRoleFilter] = useState<string>('');
	const [dateFilter, setDateFilter] = useState<string>('');

	// Pagination state
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
	const [usersPerPage] = useState<number>(10);
	const totalPages = Math.max(1, Math.ceil(filteredUsers.length / usersPerPage));

	// Current page users
	const indexOfLastUser = currentPage * usersPerPage;
	const indexOfFirstUser = indexOfLastUser - usersPerPage;
	const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

	// Format date helper
	const formatDate = (dateString: string): string => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		return date.toLocaleDateString();
	};

	// Toggle password visibility
	const togglePasswordVisibility = () => {
		setPasswordVisible(!passwordVisible);
	};

	// Clear all filters
	const clearFilters = () => {
		setSearchTerm('');
		setStatusFilter('');
		setRoleFilter('');
		setDateFilter('');
		setCurrentPage(1);
	};

	// Show delete confirmation modal
	const showDeleteConfirmation = (userId: string) => {
		setUserToDelete(userId);
		setDeleteModalOpen(true);
	};

	// Show edit user modal
	const handleShowEditModal = (user: User) => {
		setEditFormData({
			id: user.id,
			full_name: user.full_name,
			email: user.email,
			role: user.role,
		});
		setShowEditModal(true);
	};

	// Handle edit user submission
	const handleEditSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!editFormData.id) {
			toast.error('User ID is missing');
			return;
		}

		// First validate the role
		const { data: isValid, error: validationError } = await supabase.rpc('validate_user_role', {
			p_role: editFormData.role,
		});

		if (validationError) {
			toast.error(`Role validation error: ${validationError.message}`);
			return;
		}

		if (!isValid) {
			toast.error('Invalid role selected or role change not allowed');
			return;
		}

		setLoading(true);

		try {
			// Get the user_id from the profiles table first
			const { data: userData, error: userError } = await supabase
				.from('profiles')
				.select('user_id')
				.eq('id', editFormData.id)
				.single();

			if (userError) throw userError;
			if (!userData?.user_id) throw new Error('User ID not found');

			// Update the user in the profiles table
			const { data: updatedUserData, error: updateError } = await supabase
				.from('profiles')
				.update({
					full_name: editFormData.full_name,
					role: editFormData.role,
				})
				.eq('id', editFormData.id)
				.select();

			if (updateError) throw updateError;

			// Update the user_roles table
			// First delete any existing role for this user
			const { error: deleteRoleError } = await supabase
				.from('user_roles')
				.delete()
				.eq('user_id', userData.user_id);

			if (deleteRoleError) throw deleteRoleError;

			// Then insert the new role
			const { error: insertRoleError } = await supabase.from('user_roles').insert({
				user_id: userData.user_id,
				role: editFormData.role,
			});

			if (insertRoleError) throw insertRoleError;

			// Update the local state
			setUsers(prevUsers =>
				prevUsers.map(user =>
					user.id === editFormData.id
						? { ...user, full_name: editFormData.full_name, role: editFormData.role }
						: user
				)
			);

			toast.success('User updated successfully!');
			setShowEditModal(false);
		} catch (error) {
			if (error instanceof Error) {
				toast.error(`Error updating user: ${error.message}`);
			} else {
				toast.error('Error updating user');
			}
		} finally {
			setLoading(false);
		}
	};

	// Apply filters to users
	useEffect(() => {
		let result = [...users];

		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			result = result.filter(
				user =>
					user.full_name.toLowerCase().includes(searchLower) ||
					user.email.toLowerCase().includes(searchLower)
			);
		}

		if (statusFilter) {
			result = result.filter(user => user.status === statusFilter);
		}

		if (roleFilter) {
			result = result.filter(user => user.role === roleFilter);
		}

		if (dateFilter) {
			const today = new Date();
			let filterDate = new Date();

			switch (dateFilter) {
				case 'today':
					break;
				case 'yesterday':
					filterDate.setDate(filterDate.getDate() - 1);
					break;
				case 'last7days':
					filterDate.setDate(filterDate.getDate() - 7);
					break;
				case 'last30days':
					filterDate.setDate(filterDate.getDate() - 30);
					break;
				default:
					filterDate = new Date(0); // No date filter
			}

			result = result.filter(user => {
				const userDate = new Date(user.created_at);
				return userDate >= filterDate && userDate <= today;
			});
		}

		setFilteredUsers(result);
		setCurrentPage(1); // Reset to first page when filters change
	}, [users, searchTerm, statusFilter, roleFilter, dateFilter]);

	// Fetch users on component mount
	useEffect(() => {
		const fetchUsers = async () => {
			try {
				setLoading(true);
				const { data, error } = await supabase
					.from('profiles')
					.select('*')
					.order('created_at', { ascending: false });

				if (error) throw error;

				setUsers(data || []);
				const active = data?.filter(user => user.status === 'active').length || 0;
				setTotalActive(active);
				setTotalInactive((data?.length || 0) - active);
			} catch (error) {
				if (error instanceof Error) {
					toast.error(`Error fetching users: ${error.message}`);
				} else {
					toast.error('Error fetching users');
				}
			} finally {
				setLoading(false);
			}
		};

		fetchUsers();
	}, []);

	// Handle form submission for creating a new user
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);

		try {
			// Check if email already exists
			const { data: existingUser, error: checkError } = await supabase
				.from('profiles')
				.select('email')
				.eq('email', formData.email)
				.single();

			if (existingUser && !checkError) {
				throw new Error('A user with this email already exists');
			}

			// Use the new validation function instead of the old one
			const { data: isValid, error: validationError } = await supabase.rpc(
				'validate_user_creation',
				{
					p_role: formData.role,
				}
			);

			if (validationError) throw validationError;

			if (!isValid) {
				throw new Error('Invalid role selected or role creation not allowed');
			}

			// Create auth user using Supabase Auth API
			const { data: authUser, error: authError } = await supabase.auth.signUp({
				email: formData.email,
				password: formData.password,
				options: { data: { full_name: formData.full_name, role: formData.role } },
			});

			if (authError) {
				if (authError.code === 'over_email_send_rate_limit') {
					const waitTimeMatch = authError.message?.match(/after (\d+) seconds/);
					const waitTime = waitTimeMatch ? waitTimeMatch[1] : '60';
					toast.error(`Email rate limit reached. Please try again after ${waitTime} seconds.`, {
						duration: 5000,
					});
					setLoading(false);
					return;
				}
				throw authError;
			}

			if (!authUser?.user?.id) {
				throw new Error('No user ID returned from auth signup');
			}

			// Create profile in profiles table
			const { data: profileData, error: profileError } = await supabase
				.from('profiles')
				.insert([
					{
						user_id: authUser.user.id,
						full_name: formData.full_name,
						email: formData.email,
						role: formData.role,
						status: 'active',
					},
				])
				.select()
				.single();

			if (profileError) {
				// If profile creation fails, we need to clean up the auth user
				console.error('Profile creation failed:', profileError);

				// Try to delete the auth user to clean up
				try {
					// Note: This might require admin privileges
					// For now, just throw the error
				} catch (cleanupError) {
					console.error('Failed to clean up auth user:', cleanupError);
				}

				throw new Error(`Failed to create user profile: ${profileError.message}`);
			}

			// Only insert into user_roles if profile creation was successful
			// Note: user_id in user_roles should be the profiles.id, not the auth user id
			const { error: roleError } = await supabase.from('user_roles').insert([
				{
					user_id: profileData.id, // Use profiles.id, not auth user id
					role: formData.role,
					created_by: (await supabase.auth.getUser()).data.user?.id,
				},
			]);

			if (roleError) {
				console.error('Failed to insert user role:', roleError);
				// If role insertion fails, we should clean up the profile
				try {
					await supabase.from('profiles').delete().eq('id', profileData.id);
				} catch (cleanupError) {
					console.error('Failed to clean up profile after role insertion failure:', cleanupError);
				}
				throw new Error(`Failed to assign role to user: ${roleError.message}`);
			}

			toast.success('User created successfully!');
			setUsers(prevUsers => [profileData, ...prevUsers]);

			// Reset form
			setFormData({
				full_name: '',
				email: '',
				password: '',
				role: '',
			});

			// Close modal
			setShowModal(false);

			// Update counters
			setTotalActive(prev => prev + 1);
		} catch (error) {
			if (error instanceof Error) {
				toast.error(`Error creating user: ${error.message}`);
			} else {
				toast.error('Error creating user');
			}
		} finally {
			setLoading(false);
		}
	};

	// Handle user deletion
	const handleDeleteUser = async (userId: string) => {
		try {
			// First, get the user_id from profiles table to handle related tables
			const { data: userData, error: fetchError } = await supabase
				.from('profiles')
				.select('user_id, status')
				.eq('id', userId)
				.single();

			if (fetchError) throw fetchError;
			if (!userData?.user_id) throw new Error('User ID not found');

			// 1. First delete from user_roles table
			const { error: roleError } = await supabase
				.from('user_roles')
				.delete()
				.eq('user_id', userData.user_id);

			if (roleError) throw roleError;

			// 2. Then delete from profiles table
			const { error: profileError } = await supabase.from('profiles').delete().eq('id', userId);

			if (profileError) throw profileError;

			// 3. Finally delete the auth user (if needed and you have permission)
			// Note: This might require admin privileges or a server function
			// const { error: authError } = await supabase.auth.admin.deleteUser(userData.user_id);
			// if (authError) throw authError;

			// Update local state
			setUsers(prev => prev.filter(user => user.id !== userId));

			// Update counters
			if (userData.status === 'active') {
				setTotalActive(prev => prev - 1);
			} else {
				setTotalInactive(prev => prev - 1);
			}

			toast.success('User deleted successfully!');
		} catch (error) {
			if (error instanceof Error) {
				toast.error(`Error deleting user: ${error.message}`);
			} else {
				toast.error('Error deleting user');
			}
		}
	};

	// Toggle user status between active and inactive
	const toggleUserStatus = async (userId: string) => {
		try {
			const user = users.find(a => a.id === userId);
			if (!user) return;

			const newStatus = user.status === 'active' ? 'inactive' : 'active';
			const { error: userError } = await supabase
				.from('profiles')
				.update({ status: newStatus })
				.eq('id', userId);

			if (userError) throw userError;

			// Update auth metadata if user_id is available
			if (user.user_id) {
				await supabase.auth.admin
					.updateUserById(user.user_id, {
						user_metadata: { status: newStatus },
					})
					.then(({ error: _error }) => {
						if (_error) console.error('Error updating auth user status:', _error);
					});
			}

			// Update local state
			setUsers(prev => prev.map(a => (a.id === userId ? { ...a, status: newStatus } : a)));

			// Update counters
			if (newStatus === 'active') {
				setTotalActive(prev => prev + 1);
				setTotalInactive(prev => prev - 1);
			} else {
				setTotalActive(prev => prev - 1);
				setTotalInactive(prev => prev + 1);
			}

			toast.success('User status updated!');
		} catch (_error: any) {
			toast.error('Error updating user status ' + _error?.message);
		}
	};

	return (
		<div className="container mx-auto px-4 py-8">
			<DashboardHeader
				title="Users Management"
				description="Manage user accounts and permissions"
				buttonLabel="Create User"
				buttonPermission="users.create"
				onButtonAction={() => setShowModal(true)}
			/>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 mt-4">
				<UserStatCard
					label="Total Users"
					value={loading ? '...' : users.length.toString()}
					icon={<Users2 className="h-10 w-10 text-blue-500" />}
				/>
				<UserStatCard
					label="Active Users"
					value={loading ? '...' : totalActive.toString()}
					icon={<UserCheck className="h-10 w-10 text-green-500" />}
				/>
				<UserStatCard
					label="Inactive Users"
					value={loading ? '...' : totalInactive.toString()}
					icon={<UserMinus className="h-10 w-10 text-red-500" />}
				/>
			</div>

			{/* Filters */}
			<UserFilters
				searchTerm={searchTerm}
				setSearchTerm={setSearchTerm}
				statusFilter={statusFilter}
				setStatusFilter={setStatusFilter}
				roleFilter={roleFilter}
				setRoleFilter={setRoleFilter}
				dateFilter={dateFilter}
				setDateFilter={setDateFilter}
				clearFilters={clearFilters}
			/>

			{/* User Table */}
			<UserTable
				users={currentUsers}
				loading={loading}
				toggleUserStatus={toggleUserStatus}
				showDeleteConfirmation={showDeleteConfirmation}
				showEditModal={handleShowEditModal}
				formatDate={formatDate}
				currentPage={currentPage}
				totalPages={totalPages}
				setCurrentPage={setCurrentPage}
			/>

			{/* Modals */}
			<UserFormModal
				showModal={showModal}
				setShowModal={setShowModal}
				formData={formData}
				setFormData={setFormData}
				handleSubmit={handleSubmit}
				loading={loading}
				passwordVisible={passwordVisible}
				togglePasswordVisibility={togglePasswordVisibility}
			/>

			<DeleteModal
				deleteModalOpen={deleteModalOpen}
				setDeleteModalOpen={setDeleteModalOpen}
				userToDelete={userToDelete}
				handleDeleteUser={handleDeleteUser}
			/>

			<EditUserModal
				showEditModal={showEditModal}
				setShowEditModal={setShowEditModal}
				editFormData={editFormData}
				setEditFormData={setEditFormData}
				handleEditSubmit={handleEditSubmit}
				loading={loading}
			/>
		</div>
	);
};

// Export with permission guard
const UsersWithPermission = () => {
	// Debug permissions

	// Create a permission denied message component
	const PermissionDenied = () => (
		<div className="flex flex-col items-center justify-center h-full p-8 text-center">
			<div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
				<h2 className="text-xl font-bold text-red-700 mb-2">Access Denied</h2>
				<p className="text-gray-700 mb-4">
					You don't have permission to access the Users Management page.
				</p>
				<p className="text-sm text-gray-600">
					Required permission: <code className="bg-gray-100 px-1 py-0.5 rounded">users.read</code>
				</p>
			</div>
		</div>
	);

	return (
		<PermissionGuard permission="users.read" fallback={<PermissionDenied />}>
			<Users />
		</PermissionGuard>
	);
};

export default UsersWithPermission;
